import axiosInstance from './axiosInstance';

/**
 * Staffing agencies — three audiences, three API roots:
 *
 *   adminAgencyService   /admin/agencies              the platform admin creates agencies
 *   agencyService        /agency                      the agency's own workspace
 *   homeAgencyService    /carehome-manager/agencies   a care home booking cover
 *
 * Calls throw the raw axios error; pages read the message with errorMessage()
 * from pages/agency/shared.js.
 */

const unwrap = (response) => response.data;

/** Save a CSV the API sent as a file download. */
const downloadCsv = async (url, params, fallbackName) => {
  const response = await axiosInstance.get(url, { params, responseType: 'blob' });
  const disposition = response.headers?.['content-disposition'] || '';
  const name = /filename="([^"]+)"/.exec(disposition)?.[1] || fallbackName;
  const href = URL.createObjectURL(response.data);
  const a = document.createElement('a');
  a.href = href;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(href);
};

export const adminAgencyService = {
  list: async (params) => unwrap(await axiosInstance.get('/admin/agencies', { params })),
  get: async (id) => unwrap(await axiosInstance.get(`/admin/agencies/${id}`)),
  // { name, email, phone, address, admin_name, admin_email }
  create: async (payload) => unwrap(await axiosInstance.post('/admin/agencies', payload)),
  update: async (id, payload) => unwrap(await axiosInstance.put(`/admin/agencies/${id}`, payload)),
  resendInvite: async (id) => unwrap(await axiosInstance.post(`/admin/agencies/${id}/resend-invite`)),
  setActive: async (id, isActive) => unwrap(await axiosInstance.patch(`/admin/agencies/${id}/status`, { is_active: isActive })),
  remove: async (id) => unwrap(await axiosInstance.delete(`/admin/agencies/${id}`)),
};

export const agencyService = {
  overview: async () => unwrap(await axiosInstance.get('/agency/overview')),
  account: async () => unwrap(await axiosInstance.get('/agency/account')),

  connections: async () => unwrap(await axiosInstance.get('/agency/connections')),
  acceptConnection: async (id) => unwrap(await axiosInstance.post(`/agency/connections/${id}/accept`)),
  declineConnection: async (id, reason) => unwrap(await axiosInstance.post(`/agency/connections/${id}/decline`, { reason })),

  staff: async (status = 'active') => unwrap(await axiosInstance.get('/agency/staff', { params: { status } })),
  getStaff: async (id) => unwrap(await axiosInstance.get(`/agency/staff/${id}`)),
  createStaff: async (payload) => unwrap(await axiosInstance.post('/agency/staff', payload)),
  updateStaff: async (id, payload) => unwrap(await axiosInstance.put(`/agency/staff/${id}`, payload)),
  uploadDocument: async (id, { file, section, trainingId }) => {
    const form = new FormData();
    form.append('file', file);
    form.append('section', section);
    if (trainingId) form.append('training_id', trainingId);
    return unwrap(await axiosInstance.post(`/agency/staff/${id}/documents`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }));
  },
  removeDocument: async (id, { section, trainingId, path }) => unwrap(await axiosInstance.delete(`/agency/staff/${id}/documents`, {
    data: { section, training_id: trainingId, path },
  })),
  archiveStaff: async (id, reason) => unwrap(await axiosInstance.post(`/agency/staff/${id}/archive`, { reason })),
  restoreStaff: async (id) => unwrap(await axiosInstance.post(`/agency/staff/${id}/restore`)),
  resendStaffInvite: async (id) => unwrap(await axiosInstance.post(`/agency/staff/${id}/resend-invite`)),

  requests: async (params) => unwrap(await axiosInstance.get('/agency/requests', { params })),
  candidates: async (requestId) => unwrap(await axiosInstance.get(`/agency/requests/${requestId}/candidates`)),
  // { worker_ids, hourly_rate, note }
  offer: async (requestId, payload) => unwrap(await axiosInstance.post(`/agency/requests/${requestId}/offer`, payload)),
  declineRequest: async (requestId, reason) => unwrap(await axiosInstance.post(`/agency/requests/${requestId}/decline`, { reason })),
  // Several dates of one group request (same CG- reference) at once.
  groupCandidates: async (requestIds) => unwrap(await axiosInstance.get('/agency/requests/group/candidates', { params: { ids: requestIds.join(',') } })),
  // { request_ids, worker_ids, hourly_rate, note }
  offerGroup: async (payload) => unwrap(await axiosInstance.post('/agency/requests/group/offer', payload)),

  bookings: async (params) => unwrap(await axiosInstance.get('/agency/bookings', { params })),
  withdrawOffer: async (bookingId, reason) => unwrap(await axiosInstance.post(`/agency/bookings/${bookingId}/withdraw`, { reason })),
  // A CONFIRMED booking the agency can no longer supply — the home is notified.
  cancelBooking: async (bookingId, reason) => unwrap(await axiosInstance.post(`/agency/bookings/${bookingId}/cancel`, { reason })),

  hours: async (params) => unwrap(await axiosInstance.get('/agency/hours', { params })),
  exportHours: (params) => downloadCsv('/agency/hours/export', params, 'approved-hours.csv'),

  // Where the staff are and what they did (counts only — never a resident's record).
  dashboard: async () => unwrap(await axiosInstance.get('/agency/dashboard')),
  rota: async (from) => unwrap(await axiosInstance.get('/agency/rota', { params: { from } })),
  timesheets: async (params) => unwrap(await axiosInstance.get('/agency/timesheets', { params })),
  exportTimesheets: (params) => downloadCsv('/agency/timesheets/export', params, 'hours-by-staff.csv'),
  staffActivity: async (id, params) => unwrap(await axiosInstance.get(`/agency/staff/${id}/activity`, { params })),
};

const home = '/carehome-manager/agencies';

export const homeAgencyService = {
  overview: async () => unwrap(await axiosInstance.get(`${home}/overview`)),
  search: async (q) => unwrap(await axiosInstance.get(`${home}/search`, { params: { q } })),

  connections: async () => unwrap(await axiosInstance.get(`${home}/connections`)),
  requestConnection: async (agencyId, message) => unwrap(await axiosInstance.post(`${home}/connections`, { agency_id: agencyId, message })),
  cancelConnection: async (id) => unwrap(await axiosInstance.post(`${home}/connections/${id}/cancel`)),
  archiveConnection: async (id, reason) => unwrap(await axiosInstance.post(`${home}/connections/${id}/archive`, { reason })),
  restoreConnection: async (id) => unwrap(await axiosInstance.post(`${home}/connections/${id}/restore`)),

  workers: async (params) => unwrap(await axiosInstance.get(`${home}/workers`, { params })),
  worker: async (userId) => unwrap(await axiosInstance.get(`${home}/workers/${userId}`)),
  approveWorker: async (userId, canLogMedication) => unwrap(await axiosInstance.post(`${home}/workers/${userId}/approve`, { can_log_medication: canLogMedication })),
  requestWorkerChanges: async (userId, reason) => unwrap(await axiosInstance.post(`${home}/workers/${userId}/request-changes`, { reason })),
  setWorkerMedication: async (userId, value) => unwrap(await axiosInstance.patch(`${home}/workers/${userId}/medication`, { can_log_medication: value })),
  // Dashboard modules this home lets them use during their booked shifts here.
  setWorkerDashboardAccess: async (userId, menuIds) => unwrap(await axiosInstance.patch(`${home}/workers/${userId}/dashboard-access`, { menu_ids: menuIds })),

  requests: async (params) => unwrap(await axiosInstance.get(`${home}/requests`, { params })),
  // { unit, job_role, agency_ids, note, shifts: [{ date, start, end, places }] }
  createRequests: async (payload) => unwrap(await axiosInstance.post(`${home}/requests`, payload)),
  cancelRequest: async (id, reason) => unwrap(await axiosInstance.post(`${home}/requests/${id}/cancel`, { reason })),
  closeRequest: async (id, reason) => unwrap(await axiosInstance.post(`${home}/requests/${id}/close`, { reason })),

  bookings: async (params) => unwrap(await axiosInstance.get(`${home}/bookings`, { params })),
  confirm: async (id) => unwrap(await axiosInstance.post(`${home}/bookings/${id}/confirm`)),
  reject: async (id, reason) => unwrap(await axiosInstance.post(`${home}/bookings/${id}/reject`, { reason })),
  cancelBooking: async (id, reason) => unwrap(await axiosInstance.post(`${home}/bookings/${id}/cancel`, { reason })),
  noShow: async (id, note) => unwrap(await axiosInstance.post(`${home}/bookings/${id}/no-show`, { note })),
  approveHours: async (id) => unwrap(await axiosInstance.post(`${home}/bookings/${id}/approve-hours`)),
  // { clock_in: 'YYYY-MM-DDTHH:MM', clock_out, break_minutes, reason }
  recordHours: async (id, payload) => unwrap(await axiosInstance.post(`${home}/bookings/${id}/hours`, payload)),
  reopenHours: async (id, reason) => unwrap(await axiosInstance.post(`${home}/bookings/${id}/reopen-hours`, { reason })),

  hours: async (params) => unwrap(await axiosInstance.get(`${home}/hours`, { params })),
  exportHours: (params) => downloadCsv(`${home}/hours/export`, params, 'agency-hours.csv'),
  rota: async (from, to) => unwrap(await axiosInstance.get(`${home}/rota`, { params: { from, to } })),
};

export default agencyService;
